import type { RealtimeGame, RealtimeState } from "../realtime";
import { createRng } from "../rng";

/**
 * A tunnel runner — three lanes, walls to dodge, bars to jump — and the first
 * game here built to be drawn in 3D.
 *
 * The renderer is the new part; this file is deliberately not. Every value
 * below is an integer, the track is derived from the seed rather than stored,
 * and `step` reads nothing but `state`. That is what lets the Durable Object
 * replay a run and work out the score for itself, and it is the property a
 * physics engine could not have kept — which is why the game underneath the
 * WebGL is lanes and rows and whole numbers, and why it can afford to be.
 *
 * ## Distance is measured in units, not rows
 *
 * A row of track is `ZOOM_UNIT` units long and the runner covers `zoomSpeed`
 * units a step, so speed can rise smoothly without ever landing on a fraction.
 * The one rule that keeps the collision check honest is that a step never
 * covers a whole row — see `ZOOM_TOP_SPEED` — because then at most one row is
 * entered per step and nothing can be passed through unnoticed.
 */

export const ZOOM_LANES = 3;
export const ZOOM_RATE = 30;
/**
 * Thirty steps a second, so a minute at the very outside.
 *
 * Shorter than the other arcade games on purpose. Both children play the same
 * track one after the other, so the wait for your friend to finish is the run
 * length again — and a good player here survives to the clock rather than dying
 * early, which makes the worst case the usual case.
 */
export const ZOOM_MAX_STEPS = 1800;

/** How long one row of track is. Distance is counted in these. */
export const ZOOM_UNIT = 60;

export const ZOOM_START_SPEED = 12;
/**
 * The fastest it ever gets, and the reason the collision check can look at one
 * row a step. Must stay below `ZOOM_UNIT`: at or above it a step could cross
 * two rows and the second would never be checked, which is a wall you die to
 * having run straight through the one in front of it.
 */
export const ZOOM_TOP_SPEED = 24;
/**
 * Steps between one notch of speed and the next.
 *
 * Set so that flat out is actually reached — at three hundred it was not, and a
 * top speed a run never gets to is a constant pretending to be a difficulty
 * curve. Twelve notches of a hundred and twenty-five steps puts the runner at
 * full pelt with ten seconds of the clock still to go.
 */
export const ZOOM_RAMP = 125;

/** Rows between one obstacle and the next. */
export const ZOOM_SLOT = 5;
/** Clear track to begin with, so nobody is killed before they have looked up. */
export const ZOOM_FIRST_SLOT = 15;
/** How many steps a jump keeps you off the floor. */
export const ZOOM_JUMP_STEPS = 12;

/**
 * How long changing lane takes, in steps — and it is not free.
 *
 * For as long as a slide lasts the runner is across both lanes and is hit by
 * whatever is in either of them. Without that a lane change is instantaneous,
 * every wall can be dodged at the last possible moment, and the game has no
 * skill in it at all: the first cut of this ran a short-sighted computer player
 * through twenty seeds and it survived to the clock on every single one.
 *
 * Four steps is a seventh of a second, which is nothing when an obstacle is
 * eight hundred milliseconds away and is the whole game when it is four
 * hundred. Committing early is the thing being asked for.
 */
export const ZOOM_SLIDE_STEPS = 4;

/** What one gem is worth. A row of track is worth one, so distance counts too. */
export const ZOOM_GEM = 10;

export interface ZoomState extends RealtimeState {
	kind: "zoom";
	/** How far down the tunnel, in units. `ZOOM_UNIT` of them to a row. */
	travel: number;
	/** Rows entered so far — the distance half of the score. */
	row: number;
	/** The lane being headed for, which is the only one once a slide is done. */
	lane: number;
	/** The lane being left. Only meaningful while `slide` is running. */
	from: number;
	/** Steps of slide left. Above zero means straddling `from` and `lane`. */
	slide: number;
	/** Steps of air left. Zero means on the floor, and only then can you jump. */
	air: number;
	gems: number;
	/** The row the last gem came off, so the renderer can stop drawing it. */
	tookAt: number;
}

/** Sideways, or upwards. There is nothing else to do. */
export type ZoomInput = { move: "left" | "right" } | { jump: true };

/** What is sitting on one row of track. */
export interface ZoomRow {
	/** Lanes with a wall in them, as a bitmask. Never all of them. */
	walls: number;
	/** Lanes with a bar you have to be in the air to clear. */
	bars: number;
	/** Which lane holds a gem, or -1 for none. */
	gem: number;
}

const CLEAR: ZoomRow = { walls: 0, bars: 0, gem: -1 };

/** Whether there is anything on this row at all. */
export function zoomOccupied(row: ZoomRow): boolean {
	return row.walls !== 0 || row.bars !== 0 || row.gem >= 0;
}

/**
 * How fast the runner is going on a given step.
 *
 * Derived rather than stored, so there is nothing to drift: two devices on the
 * same step are going the same speed by construction. This is also the game's
 * whole difficulty curve — the track never gets nastier, it only arrives
 * sooner, which is the version a nine-year-old can feel happening to them
 * rather than one that suddenly starts killing them.
 */
export function zoomSpeed(step: number): number {
	return Math.min(ZOOM_TOP_SPEED, ZOOM_START_SPEED + Math.floor(step / ZOOM_RAMP));
}

/**
 * What is on a row, worked out from the seed and the row number.
 *
 * Nothing is kept: the track is a function, so the client can look as far ahead
 * as it likes to draw the tunnel and the server never has to trust a word of
 * what comes back about where anything was.
 *
 * Two rules make every track passable no matter how the dice fall. Walls never
 * fill all three lanes, and a gem is always put in a lane that is open — so the
 * thing a child is drawn towards is also the way through, which is how a game
 * this fast teaches itself in one go.
 */
export function zoomRow(seed: number, row: number): ZoomRow {
	if (row < ZOOM_FIRST_SLOT || row % ZOOM_SLOT !== 0) return CLEAR;

	const rng = createRng((seed + row * 2_654_435_761) >>> 0);
	const roll = rng.int(100);
	const lanes = rng.shuffle([0, 1, 2]);

	// Right the way across, so there is no dodging it — the answer is to jump.
	if (roll < 20) return { walls: 0, bars: 0b111, gem: lanes[0] };

	if (roll < 60) {
		const count = roll < 42 ? 1 : 2;
		let walls = 0;
		for (let index = 0; index < count; index++) walls |= 1 << lanes[index];
		// The last of the shuffled lanes is never walled, whether one lane is
		// blocked or two, so this gem is always somewhere it is safe to be.
		return { walls, bars: 0, gem: lanes[ZOOM_LANES - 1] };
	}

	if (roll < 75) {
		/*
		 * One wall, and bars across everything else: get out of the way *and*
		 * jump. The hardest thing on the track and the only one that asks for
		 * two different answers at once, which is what stops a run at speed
		 * from being a sequence of single reflexes.
		 *
		 * Still always passable — the walled lane is the one lane you are not
		 * being asked to be in, and being in the air is always available from
		 * the floor.
		 */
		const wall = 1 << lanes[0];
		return { walls: wall, bars: 0b111 & ~wall, gem: lanes[1] };
	}

	if (roll < 90) return { walls: 0, bars: 0, gem: lanes[0] };
	return CLEAR;
}

export const zoomLaneHas = (mask: number, lane: number): boolean => (mask & (1 << lane)) !== 0;

export const zoom: RealtimeGame<ZoomState, ZoomInput> = {
	id: "zoom",
	rate: ZOOM_RATE,
	maxSteps: ZOOM_MAX_STEPS,
	/*
	 * Two, because there are three lanes and no reason ever to ask for more
	 * than two moves. A deeper queue would let a child drumming on a button
	 * find themselves swerving to orders given half a second ago, at thirty
	 * steps a second, into a wall they had already dodged.
	 */
	maxQueued: 2,

	create(seed) {
		return {
			kind: "zoom",
			step: 0,
			seed,
			over: false,
			score: 0,
			travel: 0,
			row: 0,
			lane: 1,
			from: 1,
			slide: 0,
			air: 0,
			gems: 0,
			tookAt: -1,
		};
	},

	parseInput(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const move = (raw as { move?: unknown }).move;
		if (move === "left" || move === "right") return { move };
		return (raw as { jump?: unknown }).jump === true ? { jump: true } : null;
	},

	step(state, input) {
		if (state.over) return state;

		let lane = state.lane;
		let from = state.from;
		// Both wear off before the input is read, so a jump on the step you land
		// is allowed and a move on the step you arrive is a clean one. Landing
		// and immediately taking off again is the whole rhythm of two bars in
		// quick succession.
		let air = state.air > 0 ? state.air - 1 : 0;
		let slide = state.slide > 0 ? state.slide - 1 : 0;

		if (input !== null) {
			if ("jump" in input) {
				// Only from the floor. A second jump in mid-air would make every
				// bar free, and free obstacles are scenery.
				if (air === 0) air = ZOOM_JUMP_STEPS;
			} else {
				const wanted = lane + (input.move === "left" ? -1 : 1);
				// Off the edge of the track is not a move. Refused rather than
				// clamped so that the log means the same thing on both sides.
				if (wanted >= 0 && wanted < ZOOM_LANES) {
					from = lane;
					lane = wanted;
					slide = ZOOM_SLIDE_STEPS;
				}
			}
		}

		const travel = state.travel + zoomSpeed(state.step);
		const row = Math.floor(travel / ZOOM_UNIT);

		let gems = state.gems;
		let tookAt = state.tookAt;
		let over = false;

		// At most one row per step, guaranteed by `ZOOM_TOP_SPEED < ZOOM_UNIT`.
		if (row > state.row) {
			const ahead = zoomRow(state.seed, row);
			// Mid-slide the runner is across both lanes and is hit by whatever
			// is in either. This is what makes a late dodge fail.
			const across = slide > 0 ? (1 << lane) | (1 << from) : 1 << lane;

			if ((ahead.walls & across) !== 0) over = true;
			else if ((ahead.bars & across) !== 0 && air === 0) over = true;
			else if (ahead.gem >= 0 && (across & (1 << ahead.gem)) !== 0) {
				// Being across a gem's lane at all is enough to take it. Anything
				// stricter would have a child watch one pass through them.
				gems += 1;
				tookAt = row;
			}
		}

		return {
			...state,
			step: state.step + 1,
			travel,
			row,
			lane,
			from,
			slide,
			air,
			gems,
			tookAt,
			over,
			// Reaching a row counts even if it is the one that ends the run: the
			// distance was travelled, and taking it back would mean a child's
			// score dropping at the moment they crashed.
			score: row + gems * ZOOM_GEM,
		};
	},
};

/** One row of track, with where it is, for whatever is drawing the tunnel. */
export interface ZoomTile extends ZoomRow {
	row: number;
	/** How many rows ahead of the runner, as a fraction. Negative is behind. */
	depth: number;
	/** True once its gem has been picked up, so it is not drawn twice. */
	taken: boolean;
}

/**
 * Everything worth drawing, from just behind the runner to `rows` ahead.
 *
 * Here rather than in the client because it is knowledge about the track, and
 * the track is this file's business — the same reasoning that puts the pipe
 * positions next to the flying game. It hands back only the rows with something
 * on them, which on a full-length run is about one in five.
 */
export function zoomAhead(state: ZoomState, rows: number): ZoomTile[] {
	const tiles: ZoomTile[] = [];
	// One row behind, so a wall that has just been dodged slides past rather
	// than blinking out of existence at the moment it draws level.
	for (let row = Math.max(0, state.row - 1); row <= state.row + rows; row++) {
		const tile = zoomRow(state.seed, row);
		if (!zoomOccupied(tile)) continue;
		tiles.push({
			...tile,
			row,
			depth: (row * ZOOM_UNIT - state.travel) / ZOOM_UNIT,
			taken: row === state.tookAt,
		});
	}
	return tiles;
}

/**
 * Where the runner is across the track, as a lane number that may be a
 * fraction while a slide is running.
 *
 * Presentation, but it belongs next to the rule it draws: a runner shown
 * snapping between lanes would be a runner that looks safe at the exact moment
 * the game is treating it as standing in two places at once, which is the one
 * thing a player must never have to be told.
 */
export function zoomLaneAt(state: ZoomState): number {
	if (state.slide <= 0) return state.lane;
	const through = (ZOOM_SLIDE_STEPS - state.slide) / ZOOM_SLIDE_STEPS;
	return state.from + (state.lane - state.from) * through;
}

/** How high off the floor a jump is, from 0 to 1, given the air left in it. */
export function zoomHop(air: number): number {
	if (air <= 0) return 0;
	const through = (ZOOM_JUMP_STEPS - air) / ZOOM_JUMP_STEPS;
	// A parabola through nought at both ends, so it leaves the floor and lands
	// on it rather than snapping to either.
	return 4 * through * (1 - through);
}

/** How far ahead Robo bothers to look, in rows. */
const ROBO_LOOKAHEAD = 7;

/**
 * How close a wall has to be before Robo does anything about it, in steps.
 *
 * This is the whole of Robo's fallibility, and it is deliberate. Nothing on
 * this track is unsurvivable on its own, so a policy that acted the moment it
 * saw something would simply never lose — which is exactly what the flying
 * game shipped with, and why Robo there scores the same number every single
 * race. Reacting late means a slide sometimes has not finished when the wall
 * arrives, and how often that happens depends on the seed, which is what makes
 * racing it feel like racing somebody.
 */
const ROBO_REACTION = 6;

const toward = (from: number, to: number): ZoomInput | null => {
	if (to < from) return { move: "left" };
	if (to > from) return { move: "right" };
	return null;
};

/**
 * Where to be on a row with a wall in your lane.
 *
 * The gem first, when there is one, because the track only ever puts gems in
 * open lanes — so it is both the way out and a point. That is not merely tidy:
 * picking the nearest open lane instead let the dodge and the gem want
 * opposite things, and Robo swung between them one step at a time until the
 * slide was still running when the wall arrived. Four seeds out of twenty died
 * that way, all of them to the same shape of row.
 */
function openLane(tile: ZoomRow, from: number): number {
	if (tile.gem >= 0 && !zoomLaneHas(tile.walls, tile.gem)) return tile.gem;
	for (let away = 1; away < ZOOM_LANES; away++) {
		for (const lane of [from - away, from + away]) {
			if (lane < 0 || lane >= ZOOM_LANES) continue;
			if (!zoomLaneHas(tile.walls, lane)) return lane;
		}
	}
	return from;
}

/**
 * Robo, who deals with the next thing in the way and nothing beyond it.
 *
 * Short-sighted on purpose. Every obstacle here is survivable on its own, so a
 * policy that planned two slots ahead would simply never lose, and the flying
 * game already taught me what that is worth: a computer opponent that scores
 * the same number every single race stops reading as an opponent at all. This
 * one takes the open lane in front of it, which is sometimes the wrong lane for
 * what comes after, and so its runs differ from seed to seed the way a child's
 * do.
 */
export function zoomRunner(): (state: ZoomState) => ZoomInput | null {
	return (state) => {
		const speed = zoomSpeed(state.step);

		for (let row = state.row + 1; row <= state.row + ROBO_LOOKAHEAD; row++) {
			const tile = zoomRow(state.seed, row);
			if (!zoomOccupied(tile)) continue;

			const stepsAway = Math.ceil((row * ZOOM_UNIT - state.travel) / speed);

			if (zoomLaneHas(tile.walls, state.lane)) {
				// Nothing until it is close, which is what lets a slide run out
				// of road. Checked before the bars so that on a row asking for
				// both, getting out of the wall comes first — there is no jump
				// that saves you from a wall.
				if (stepsAway > ROBO_REACTION) return null;
				return toward(state.lane, openLane(tile, state.lane));
			}
			// Late enough to still be off the floor when it arrives, which a jump
			// taken the instant one is spotted would not be.
			if (
				zoomLaneHas(tile.bars, state.lane) &&
				state.air === 0 &&
				stepsAway <= ZOOM_JUMP_STEPS - 2
			) {
				return { jump: true };
			}
			/*
			 * A gem is worth a detour, but only across a row with nothing to
			 * dodge on it — and not a swerve taken so late that the slide is
			 * still running when the row arrives.
			 *
			 * Chasing one across a row that also has a wall is what made Robo
			 * dither: out of the wall, back for the gem, out again, until it was
			 * halfway between two lanes with the wall in one of them.
			 */
			if (tile.walls === 0 && tile.gem >= 0 && stepsAway > ZOOM_SLIDE_STEPS) {
				return toward(state.lane, tile.gem);
			}
			return null;
		}
		return null;
	};
}
